import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Insumo, Recipe } from '../models/insumo.model';
import { DatabaseService } from './database.service';

@Injectable({
  providedIn: 'root'
})
export class InsumosService {
  private insumosSubject = new BehaviorSubject<Insumo[]>([]);
  public insumos$ = this.insumosSubject.asObservable();

  constructor(private database: DatabaseService) {
    setTimeout(() => this.loadInsumos(), 300);
  }

  private loadInsumos(): void {
    this.database.getAllInsumos().subscribe({
      next: (list) => {
        const items = (list || []).map((x: any) => this.deserialize(x));
        this.insumosSubject.next(items);
      },
      error: () => this.insumosSubject.next([])
    });
  }

  private deserialize(data: any): Insumo {
    const i = new Insumo(data.id, data.name, data.unit, data.quantity ?? 0);
    i.minStock = data.minStock;
    i.active = data.active !== false;
    i.createdAt = data.createdAt ? new Date(data.createdAt) : new Date();
    i.updatedAt = data.updatedAt ? new Date(data.updatedAt) : new Date();
    return i;
  }

  private serialize(i: Insumo): any {
    return {
      id: i.id,
      name: i.name,
      unit: i.unit,
      quantity: i.quantity,
      minStock: i.minStock,
      active: i.active !== false,
      createdAt: i.createdAt.toISOString(),
      updatedAt: i.updatedAt.toISOString()
    };
  }

  getInsumos(): Insumo[] {
    return this.insumosSubject.value.filter(x => x.active !== false);
  }

  /**
   * Porciones disponibles de un plato según receta e inventario actual.
   * Retorna el mínimo de floor(insumo.quantity / quantityPerUnit) por línea de receta.
   * Retorna null si no hay receta o está vacía (se considera sin límite).
   * Rápido y síncrono para usar al crear pedidos; combinar con insumos$ para tiempo real.
   */
  getAvailablePortionsFromRecipe(recipe: Recipe | null | undefined, insumos: Insumo[]): number | null {
    if (!recipe?.items?.length) return null;
    let min = Infinity;
    for (const line of recipe.items) {
      const insumo = insumos.find(i => i.id === line.insumoId);
      const available = insumo ? Math.floor(insumo.quantity / line.quantityPerUnit) : 0;
      min = Math.min(min, available);
    }
    return min === Infinity ? null : min;
  }

  getInsumoById(id: string): Insumo | undefined {
    return this.insumosSubject.value.find(x => x.id === id);
  }

  createInsumo(insumo: Insumo): void {
    insumo.updatedAt = new Date();
    this.database.createInsumo(this.serialize(insumo)).subscribe({
      next: () => {
        this.insumosSubject.next([...this.insumosSubject.value, insumo]);
      },
      error: (e) => console.error('Error creating insumo:', e)
    });
  }

  updateInsumo(insumo: Insumo): void {
    insumo.updatedAt = new Date();
    this.database.updateInsumo(this.serialize(insumo)).subscribe({
      next: () => {
        const list = this.insumosSubject.value;
        const idx = list.findIndex(x => x.id === insumo.id);
        if (idx !== -1) {
          list[idx] = insumo;
          this.insumosSubject.next([...list]);
        }
      },
      error: (e) => console.error('Error updating insumo:', e)
    });
  }

  /** Añadir stock (comprar). */
  addStock(insumoId: string, quantity: number): void {
    const insumo = this.getInsumoById(insumoId);
    if (!insumo) return;
    insumo.quantity += quantity;
    insumo.updatedAt = new Date();
    this.updateInsumo(insumo);
  }

  getAllRecipes(): Observable<Recipe[]> {
    return this.database.getAllRecipes();
  }

  getRecipe(menuItemId: string): Observable<Recipe | null> {
    return this.database.getRecipe(menuItemId);
  }

  setRecipe(menuItemId: string, items: { insumoId: string; quantityPerUnit: number }[]): void {
    this.database.setRecipe(menuItemId, items).subscribe({
      error: (e) => console.error('Error setting recipe:', e)
    });
  }

  /** Disminuir insumos cuando se entrega un plato (según receta). */
  decreaseInsumosForMenuItem(menuItemId: string, dishQuantity: number): void {
    this.database.getRecipe(menuItemId).subscribe({
      next: (recipe) => {
        if (!recipe?.items?.length) return;
        recipe.items.forEach(line => {
          const insumo = this.getInsumoById(line.insumoId);
          if (!insumo) return;
          const toDecrease = line.quantityPerUnit * dishQuantity;
          insumo.quantity = Math.max(0, insumo.quantity - toDecrease);
          insumo.updatedAt = new Date();
          this.updateInsumo(insumo);
        });
      }
    });
  }

  reloadInsumosFromDb(): void {
    this.loadInsumos();
  }
}
